// otp-whatsapp.ts — connexion par numéro + code reçu sur WHATSAPP. Module PUR (aucun accès réseau,
// aucune variable d'environnement, aucune API propre à Deno) : il tourne tel quel dans une edge
// function, dans les consoles React (import direct, comme `tarification.ts`) et dans Vitest/Deno.
//
// ⚠️ COPIE OCTET POUR OCTET dans SwiftEats ET SwiftDeliver (et bientôt SwiftShop) :
//   SwiftEats/supabase/functions/_shared/otp-whatsapp.ts
//   SwiftDeliver/supabase/functions/_shared/otp-whatsapp.ts
// Les deux fichiers de test (`otp-whatsapp.test.ts`) sont identiques aussi. Modifier l'un = modifier
// l'autre (`cmp` doit rester muet). Le jumeau Kotlin (apps natives) est
// `partage/.../data/NumeroTelephone.kt` : même table de pays, mêmes règles, mêmes vecteurs.
//
// Ce que le module fait :
//   1. la TABLE DES PAYS (code ISO, nom français, indicatif, longueurs, préfixe de tronc) ;
//   2. la normalisation E.164 et la validation d'un numéro saisi, d'après cette table ;
//   3. le corps d'un message « modèle AUTHENTICATION » de l'API WhatsApp Cloud (Meta) ;
//   4. la vérification de la signature « Standard Webhooks » du Send SMS Hook de Supabase Auth ;
//   5. la traduction en français des erreurs de Meta.

// ═══════════════════════════════════════════════════════════════════════════
//  1. Table des pays — AJOUTER UN PAYS = AJOUTER UNE LIGNE (ici, et dans le jumeau Kotlin).
// ═══════════════════════════════════════════════════════════════════════════

export interface Pays {
  /** Code ISO 3166-1 alpha-2. */
  iso: string;
  /** Nom français, tel qu'affiché dans le sélecteur. */
  nom: string;
  /** Indicatif international, sans « + ». */
  indicatif: string;
  /** Longueurs admises du numéro NATIONAL (sans indicatif, sans préfixe de tronc). */
  longueurs: number[];
  /** Expression (sans ancres) que doit respecter le numéro national ; vide = aucune contrainte de forme. */
  motif: string;
  /** true : les numéros nationaux s'écrivent avec un « 0 » devant (préfixe de tronc), absent du format international. */
  tronc: boolean;
  /** Numéro national d'exemple (placeholder du champ). */
  exemple: string;
}

/** Pays dont le numéro est l'identifiant par défaut de la plateforme (marché de départ : le Mali). */
export const PAYS_PAR_DEFAUT = "ML";

export const PAYS: readonly Pays[] = [
  { iso: "ML", nom: "Mali", indicatif: "223", longueurs: [8], motif: "[2-9]\\d{7}", tronc: false, exemple: "70 12 34 56" },
  { iso: "SN", nom: "Sénégal", indicatif: "221", longueurs: [9], motif: "[3-9]\\d{8}", tronc: false, exemple: "77 123 45 67" },
  { iso: "CI", nom: "Côte d'Ivoire", indicatif: "225", longueurs: [10], motif: "\\d{10}", tronc: false, exemple: "07 12 34 56 78" },
  { iso: "BF", nom: "Burkina Faso", indicatif: "226", longueurs: [8], motif: "[2-9]\\d{7}", tronc: false, exemple: "70 12 34 56" },
  { iso: "NE", nom: "Niger", indicatif: "227", longueurs: [8], motif: "[2-9]\\d{7}", tronc: false, exemple: "90 12 34 56" },
  { iso: "GN", nom: "Guinée", indicatif: "224", longueurs: [9], motif: "[3-9]\\d{8}", tronc: false, exemple: "621 12 34 56" },
  { iso: "BJ", nom: "Bénin", indicatif: "229", longueurs: [8, 10], motif: "\\d{8}|01\\d{8}", tronc: false, exemple: "01 90 12 34 56" },
  { iso: "TG", nom: "Togo", indicatif: "228", longueurs: [8], motif: "[2-9]\\d{7}", tronc: false, exemple: "90 12 34 56" },
  { iso: "CM", nom: "Cameroun", indicatif: "237", longueurs: [9], motif: "[2368]\\d{8}", tronc: false, exemple: "6 71 23 45 67" },
  { iso: "GA", nom: "Gabon", indicatif: "241", longueurs: [7, 8], motif: "\\d{7,8}", tronc: false, exemple: "06 12 34 56" },
  { iso: "CG", nom: "Congo", indicatif: "242", longueurs: [9], motif: "\\d{9}", tronc: false, exemple: "06 123 45 67" },
  { iso: "CD", nom: "RDC", indicatif: "243", longueurs: [9], motif: "[1-9]\\d{8}", tronc: true, exemple: "81 234 56 78" },
  { iso: "MR", nom: "Mauritanie", indicatif: "222", longueurs: [8], motif: "[2-9]\\d{7}", tronc: false, exemple: "22 12 34 56" },
  { iso: "FR", nom: "France", indicatif: "33", longueurs: [9], motif: "[1-9]\\d{8}", tronc: true, exemple: "6 12 34 56 78" },
  { iso: "BE", nom: "Belgique", indicatif: "32", longueurs: [8, 9], motif: "[1-9]\\d{7,8}", tronc: true, exemple: "470 12 34 56" },
  { iso: "ES", nom: "Espagne", indicatif: "34", longueurs: [9], motif: "[5-9]\\d{8}", tronc: false, exemple: "612 34 56 78" },
  { iso: "IT", nom: "Italie", indicatif: "39", longueurs: [6, 7, 8, 9, 10, 11], motif: "[038]\\d{5,10}", tronc: false, exemple: "312 345 6789" },
  { iso: "US", nom: "États-Unis", indicatif: "1", longueurs: [10], motif: "[2-9]\\d{2}[2-9]\\d{6}", tronc: false, exemple: "415 555 2671" },
  { iso: "CA", nom: "Canada", indicatif: "1", longueurs: [10], motif: "[2-9]\\d{2}[2-9]\\d{6}", tronc: false, exemple: "514 555 0199" },
];

export function trouverPays(iso: string | null | undefined): Pays | null {
  const code = String(iso ?? "").trim().toUpperCase();
  return PAYS.find((p) => p.iso === code) ?? null;
}

/**
 * Pays proposé d'office dans le sélecteur.
 *   1. la région de l'appareil, SI elle figure dans la table (un utilisateur à Paris voit +33) ;
 *   2. sinon le pays de configuration (`platform_config.pays_defaut` côté Eats, `organizations.country`
 *      de l'organisation opératrice côté Deliver) ;
 *   3. sinon le pays de départ de la plateforme (`PAYS_PAR_DEFAUT`).
 * La région de l'appareil n'est consultée que si aucun choix mémorisé n'existe : l'appelant ne
 * l'envoie pas quand l'utilisateur a déjà choisi un pays (consoles : jamais envoyée).
 */
export function paysParDefaut(config?: string | null, regionAppareil?: string | null): Pays {
  return trouverPays(regionAppareil) ?? trouverPays(config) ?? trouverPays(PAYS_PAR_DEFAUT)!;
}

/** Pays dont l'indicatif commence le numéro international (`chiffres` sans « + »), le plus long d'abord. */
function paysDeIndicatif(chiffres: string, prefere?: Pays | null): Pays | null {
  const candidats = PAYS.filter((p) => chiffres.startsWith(p.indicatif));
  if (candidats.length === 0) return null;
  const maxLen = Math.max(...candidats.map((p) => p.indicatif.length));
  const memeLongueur = candidats.filter((p) => p.indicatif.length === maxLen);
  // +1 sert les États-Unis ET le Canada : on garde le pays choisi s'il partage l'indicatif.
  return memeLongueur.find((p) => prefere && p.iso === prefere.iso) ?? memeLongueur[0] ?? null;
}

// ═══════════════════════════════════════════════════════════════════════════
//  2. Normalisation E.164
// ═══════════════════════════════════════════════════════════════════════════

export type CodeErreurNumero =
  | "vide"
  | "caracteres"
  | "trop_court"
  | "trop_long"
  | "format"
  | "pays_inconnu";

export type ResultatNumero =
  | {
    ok: true;
    /** « +22370123456 » */
    e164: string;
    /** Forme stockée par Supabase Auth et attendue par Meta : « 22370123456 » (sans « + »). */
    sansPlus: string;
    iso: string;
    indicatif: string;
    /** Numéro national, sans préfixe de tronc. */
    national: string;
  }
  | { ok: false; code: CodeErreurNumero; message: string };

export const MESSAGES_NUMERO: Record<CodeErreurNumero, string> = {
  vide: "Saisissez votre numéro de téléphone.",
  caracteres: "Le numéro ne doit contenir que des chiffres, des espaces et un « + » au début.",
  trop_court: "Ce numéro est trop court.",
  trop_long: "Ce numéro est trop long.",
  format: "Ce numéro n'est pas valide pour ce pays.",
  pays_inconnu: "Pays non pris en charge pour le moment. Choisissez un autre pays ou utilisez votre e-mail.",
};

const erreur = (code: CodeErreurNumero): ResultatNumero => ({ ok: false, code, message: MESSAGES_NUMERO[code] });

function valider(p: Pays, national: string): ResultatNumero {
  const min = Math.min(...p.longueurs);
  const max = Math.max(...p.longueurs);
  if (national.length < min) return erreur("trop_court");
  if (national.length > max) return erreur("trop_long");
  if (!p.longueurs.includes(national.length)) return erreur("format");
  if (p.motif && !new RegExp("^(?:" + p.motif + ")$").test(national)) return erreur("format");
  return {
    ok: true,
    e164: "+" + p.indicatif + national,
    sansPlus: p.indicatif + national,
    iso: p.iso,
    indicatif: p.indicatif,
    national,
  };
}

/**
 * Numéro saisi → E.164, d'après la table des pays.
 *
 * `isoChoisi` = pays du sélecteur (valeur par défaut : `PAYS_PAR_DEFAUT`). Il sert aux numéros
 * ÉCRITS EN LOCAL ; un numéro qui porte déjà son indicatif (« + » ou « 00 ») est reconnu seul.
 *
 *  • « 70 12 34 56 »                     + Mali  → +22370123456
 *  • « +223 70-12-34-56 », « 00223 7012… » → +22370123456 (quel que soit le pays du sélecteur)
 *  • « 06 12 34 56 78 »                  + France → +33612345678 (le « 0 » de tronc est retiré)
 *  • « +33 (0)6 12 34 56 78 »            → +33612345678 (le « (0) » écrit à tort est retiré aussi)
 *  • « 22370123456 » (indicatif collé, sans « + ») + Mali → +22370123456
 *  • tout autre indicatif que ceux de la table → refus `pays_inconnu` (jamais de devinette)
 */
export function normaliserTelephone(brut: string | null | undefined, isoChoisi: string = PAYS_PAR_DEFAUT): ResultatNumero {
  const texte = String(brut ?? "").replace(/[  ]/g, " ").trim();
  if (!texte || texte === "+") return erreur("vide");
  // Seuls chiffres, espaces, tirets, points, parenthèses et un « + » initial : le reste est une faute de saisie
  // (une lettre dans un numéro n'est jamais un numéro).
  if (!/^\+?[\d\s().\-]+$/.test(texte)) return erreur("caracteres");
  const choisi = trouverPays(isoChoisi) ?? trouverPays(PAYS_PAR_DEFAUT)!;

  // « (0) » après un indicatif : on le retire avant de compter (« +33 (0)6… »).
  const sansZeroParenthese = texte.replace(/\(\s*0\s*\)/g, "");
  const chiffres = sansZeroParenthese.replace(/\D+/g, "");
  if (!chiffres) return erreur("vide");

  let international: string | null = null;
  if (sansZeroParenthese.startsWith("+")) international = chiffres;
  else if (chiffres.startsWith("00")) international = chiffres.slice(2);

  if (international === null) {
    // Indicatif collé sans « + » : seulement si la longueur correspond EXACTEMENT à un numéro complet
    // du pays choisi (sinon « 33612345678 » saisi au Mali serait pris pour un numéro malien).
    const collé = choisi.longueurs.some((l) => chiffres.length === choisi.indicatif.length + l) &&
      chiffres.startsWith(choisi.indicatif);
    if (collé) {
      const r = valider(choisi, chiffres.slice(choisi.indicatif.length));
      if (r.ok) return r;
    }
    let national = chiffres;
    if (choisi.tronc && national.startsWith("0")) national = national.slice(1);
    return valider(choisi, national);
  }

  const pays = paysDeIndicatif(international, choisi);
  if (!pays) return erreur(international.length < 8 ? "trop_court" : "pays_inconnu");
  let national = international.slice(pays.indicatif.length);
  // Les gens écrivent « +33 0612… » : le tronc n'a rien à faire derrière un indicatif.
  if (pays.tronc && national.startsWith("0")) national = national.slice(1);
  return valider(pays, national);
}

/** « +22370123456 » → « +223 70 12 34 56 » (affichage ; paires depuis la gauche, dernier groupe éventuellement de 3). */
export function afficherTelephone(e164: string | null | undefined): string {
  const chiffres = String(e164 ?? "").replace(/\D+/g, "");
  if (!chiffres) return "";
  const pays = paysDeIndicatif(chiffres);
  if (!pays) return "+" + chiffres;
  const national = chiffres.slice(pays.indicatif.length);
  const groupes: string[] = [];
  for (let i = 0; i < national.length; i += 2) groupes.push(national.slice(i, i + 2));
  if (groupes.length > 1 && groupes[groupes.length - 1]!.length === 1) {
    const dernier = groupes.pop() ?? "";
    const avant = groupes.pop() ?? "";
    groupes.push(avant + dernier);
  }
  return "+" + pays.indicatif + " " + groupes.join(" ");
}

/**
 * Numéro tel que Supabase Auth le stocke / le transmet au hook (« 22370123456 » ou « +22370123456 »)
 * → E.164 « +… », ou null. Contrôle MINIMAL (8 à 15 chiffres, pas d'indicatif 0) : le hook ne rejette
 * pas un compte existant parce que son pays a quitté la table.
 */
export function e164DepuisAuth(valeur: string | null | undefined): string | null {
  const chiffres = String(valeur ?? "").replace(/\D+/g, "");
  if (chiffres.length < 8 || chiffres.length > 15 || chiffres.startsWith("0")) return null;
  return "+" + chiffres;
}

/** Numéro masqué pour les journaux : « +223••••56 ». Jamais le numéro entier dans un log. */
export function masquerTelephone(valeur: string | null | undefined): string {
  const chiffres = String(valeur ?? "").replace(/\D+/g, "");
  if (chiffres.length < 6) return "••••";
  return "+" + chiffres.slice(0, 3) + "••••" + chiffres.slice(-2);
}

// ═══════════════════════════════════════════════════════════════════════════
//  3. Message WhatsApp « AUTHENTICATION » (bouton « Copier le code »)
// ═══════════════════════════════════════════════════════════════════════════

export const LANGUE_PAR_DEFAUT = "fr";

/**
 * Corps de la requête `POST /{phone-id}/messages` pour un modèle d'authentification.
 * Meta exige le code À DEUX ENDROITS : dans le corps du modèle (`{{1}}`) ET dans le bouton « Copier le
 * code », que l'API stocke comme un bouton URL (`sub_type: "url"`, index 0). Il omettre l'un des deux
 * donne l'erreur 131008 / 132000.
 */
export function corpsCodeWhatsapp(numeroSansPlus: string, code: string, modele: string, langue: string = LANGUE_PAR_DEFAUT) {
  return {
    messaging_product: "whatsapp",
    recipient_type: "individual",
    to: numeroSansPlus,
    type: "template",
    template: {
      name: modele,
      language: { code: langue },
      components: [
        { type: "body", parameters: [{ type: "text", text: code }] },
        { type: "button", sub_type: "url", index: "0", parameters: [{ type: "text", text: code }] },
      ],
    },
  };
}

/** Texte à soumettre à Meta pour le modèle (catégorie AUTHENTICATION, langue fr). Le corps, lui, est fixé par Meta. */
export const TEXTE_MODELE_AUTHENTIFICATION = "{{1}} est votre code de vérification.";

// ═══════════════════════════════════════════════════════════════════════════
//  4. Signature « Standard Webhooks » du Send SMS Hook de Supabase Auth
// ═══════════════════════════════════════════════════════════════════════════

const TOLERANCE_SECONDES = 5 * 60;

function base64VersOctets(b64: string) {
  const bin = atob(b64.replace(/\s+/g, ""));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function octetsVersBase64(octets: Uint8Array): string {
  let s = "";
  for (const o of octets) s += String.fromCharCode(o);
  return btoa(s);
}

/** Comparaison en temps constant (la signature d'un webhook ne se compare pas avec `===`). */
function egalConstant(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** Clé HMAC tirée du secret « v1,whsec_<base64> » que Supabase affiche à la création du hook. */
function cleDuSecret(secret: string) {
  const brut = secret.trim().replace(/^v1,/, "").replace(/^whsec_/, "");
  return base64VersOctets(brut);
}

/** Signature attendue pour un message (utile aux tests : on signe comme Supabase). */
export async function signerWebhook(secret: string, id: string, horodatage: number | string, corps: string): Promise<string> {
  const cle = await crypto.subtle.importKey(
    "raw", cleDuSecret(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"],
  );
  const signature = await crypto.subtle.sign("HMAC", cle, new TextEncoder().encode(`${id}.${horodatage}.${corps}`));
  return "v1," + octetsVersBase64(new Uint8Array(signature));
}

export type VerificationSignature = { ok: true } | { ok: false; raison: "en_tetes_absents" | "horodatage" | "expire" | "signature" | "secret" };

/**
 * Vérifie un appel du hook. `entetes` : `webhook-id`, `webhook-timestamp`, `webhook-signature` (casse libre).
 * Refuse tout appel plus ancien (ou plus lointain dans le futur) que 5 minutes : un message intercepté ne
 * se rejoue pas. `maintenant` (secondes) sert aux tests.
 */
export async function verifierSignatureHook(
  corps: string,
  entetes: Headers | Record<string, string | null | undefined>,
  secret: string | null | undefined,
  maintenant: number = Math.floor(Date.now() / 1000),
): Promise<VerificationSignature> {
  if (!secret || !secret.trim()) return { ok: false, raison: "secret" };
  const lire = (nom: string): string | null => {
    if (typeof Headers !== "undefined" && entetes instanceof Headers) return entetes.get(nom);
    const table = entetes as Record<string, string | null | undefined>;
    for (const k of Object.keys(table)) if (k.toLowerCase() === nom) return table[k] ?? null;
    return null;
  };
  const id = lire("webhook-id");
  const ts = lire("webhook-timestamp");
  const sigs = lire("webhook-signature");
  if (!id || !ts || !sigs) return { ok: false, raison: "en_tetes_absents" };
  const t = Number(ts);
  if (!Number.isFinite(t) || !/^\d+$/.test(ts)) return { ok: false, raison: "horodatage" };
  if (Math.abs(maintenant - t) > TOLERANCE_SECONDES) return { ok: false, raison: "expire" };

  let attendue: string;
  try {
    attendue = await signerWebhook(secret, id, ts, corps);
  } catch {
    return { ok: false, raison: "secret" };
  }
  // L'en-tête peut contenir plusieurs signatures séparées par des espaces (rotation de secret).
  for (const candidate of sigs.split(" ")) {
    if (egalConstant(candidate.trim(), attendue)) return { ok: true };
  }
  return { ok: false, raison: "signature" };
}

/**
 * Extrait numéro et code du corps du hook, ou null si la forme est inattendue.
 *
 * Forme réelle observée sur GoTrue v2.192 (essai du 10/10/2026) : `{ metadata, user: {...}, sms: { otp, phone } }`.
 * ⚠️ Le numéro à joindre est `sms.phone` : à l'AJOUT d'un numéro sur un compte e-mail (`updateUser({ phone })`),
 * `user.phone` est VIDE et le nouveau numéro est dans `user.new_phone` (et `sms.phone`). On lit donc, dans l'ordre :
 * `sms.phone`, `user.new_phone`, `user.phone`.
 */
export function lireDemandeHook(corps: string): { telephone: string; code: string } | null {
  try {
    const j = JSON.parse(corps);
    const brut = [j?.sms?.phone, j?.user?.new_phone, j?.user?.phone].find((v) => typeof v === "string" && v.trim() !== "");
    const code = j?.sms?.otp;
    if (typeof brut !== "string" || typeof code !== "string") return null;
    if (!/^\d{4,10}$/.test(code)) return null;
    const e164 = e164DepuisAuth(brut);
    if (!e164) return null;
    return { telephone: e164, code };
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  5. Erreurs de Meta → phrases françaises
// ═══════════════════════════════════════════════════════════════════════════

export type CategorieErreurMeta =
  | "numero_sans_whatsapp"
  | "trop_de_messages"
  | "numero_non_autorise"
  | "configuration"
  | "indisponible";

export interface ErreurWhatsapp {
  categorie: CategorieErreurMeta;
  /** Statut HTTP à renvoyer au hook (et donc à l'app). */
  http: number;
  /** Phrase pour l'utilisateur. Contient TOUJOURS le mot « WhatsApp » : c'est ainsi que les apps la reconnaissent. */
  message: string;
}

const MSG = {
  numero_sans_whatsapp: "Ce numéro n'est pas joignable sur WhatsApp. Vérifiez le numéro ou utilisez votre adresse e-mail.",
  trop_de_messages: "Trop de codes demandés. Patientez quelques minutes avant de redemander un code WhatsApp.",
  numero_non_autorise: "Ce numéro n'est pas encore autorisé à recevoir des codes WhatsApp (mode test). Utilisez votre adresse e-mail.",
  configuration: "L'envoi du code par WhatsApp n'est pas disponible pour le moment. Utilisez votre adresse e-mail.",
  indisponible: "Le code n'a pas pu être envoyé sur WhatsApp. Réessayez dans un instant ou utilisez votre adresse e-mail.",
} as const;

/**
 * Code d'erreur de l'API Cloud (`error.code`) et statut HTTP → catégorie + phrase.
 * Codes retenus (documentation Meta, « Cloud API error codes ») :
 *   131026 message non distribuable (numéro sans WhatsApp) · 133010 numéro non enregistré
 *   131030 destinataire hors liste autorisée (mode développement) · 131042 problème de paiement
 *   130429 / 131056 / 80007 / 4 / 17 / 613 limites de débit
 *   190 / 102 / 10 / 200 / 368 jeton ou permission · 100 / 131008 / 131009 / 132000 / 132001 / 132005 /
 *   132007 / 132012 / 132015 / 132016 / 131037 / 131045 modèle ou paramètres (configuration à corriger)
 */
export function traduireErreurMeta(statutHttp: number, codeMeta: number | string | null | undefined): ErreurWhatsapp {
  const code = Number(codeMeta);
  const fait = (categorie: CategorieErreurMeta, http: number): ErreurWhatsapp => ({ categorie, http, message: MSG[categorie] });
  if ([131026, 133010, 131021].includes(code)) return fait("numero_sans_whatsapp", 422);
  if (code === 131030) return fait("numero_non_autorise", 422);
  if ([130429, 131056, 80007, 4, 17, 613, 80008].includes(code) || statutHttp === 429) return fait("trop_de_messages", 429);
  if ([190, 102, 10, 200, 368, 100, 131008, 131009, 131037, 131042, 131045, 132000, 132001, 132005, 132007, 132012, 132015, 132016].includes(code)) {
    return fait("configuration", 503);
  }
  if (statutHttp === 401 || statutHttp === 403 || statutHttp === 404) return fait("configuration", 503);
  return fait("indisponible", 502);
}

/**
 * Corps d'erreur attendu par Supabase Auth pour un hook. ⚠️ À renvoyer avec le statut HTTP **200** : GoTrue
 * (vérifié le 10/10/2026) transforme tout autre statut en « Unexpected status code returned from hook » (500) et
 * perd le message ; avec 200 + `{ error }`, il répond à l'app avec `http_code` et NOTRE phrase.
 */
export function erreurHook(http: number, message: string) {
  return { error: { http_code: http, message } };
}
